import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ApiError, createApiClient } from '../api/client';
import type { RequestOptions } from '../api/client';
import { compassApi } from '../api/contracts';
import { i18n } from '../i18n';
import { formatApiError } from '../utils/api-error';
import { readStorage, writeStorage } from '../utils/storage';
export interface Subject {
  user_id: string; username: string; display_name: string; tenant_id: string;
  roles: string[]; permissions: string[]; business_line_ids: string[];
  org_scope: string[]; classifications: string[]; policy_version: string;
  expires_at: string; session_id: string;
}
const TOKEN_KEY = 'compass.session';
export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(readStorage('sessionStorage', TOKEN_KEY));
  const me = ref<Subject | null>(null);
  const error = ref<string | null>(null);
  const isAuthed = computed(() => !!token.value && !!me.value);
  let fetchingMe: Promise<void> | null = null;
  function clearSession() {
    token.value = null; me.value = null;
    writeStorage('sessionStorage', TOKEN_KEY, null);
  }
  const client = createApiClient(() => token.value, () => {
    clearSession(); window.dispatchEvent(new Event('compass:session-expired'));
  });
  const api = <T = any>(path: string, body?: unknown, method?: string, options?: RequestOptions) => client.request<T>(path, body, method, options);
  const service = compassApi(client.request);
  const permitted = (permission: string) => me.value?.permissions?.some(p => p === '*' || p === permission) ?? false;
  async function fetchMe() {
    if (!token.value) return;
    if (fetchingMe) return fetchingMe;
    const currentToken = token.value;
    fetchingMe = (async () => {
      try { const subject = await api<Subject>('/auth/me'); if (token.value === currentToken) me.value = subject; }
      catch (cause) { if (cause instanceof ApiError && cause.status === 401) clearSession(); else throw cause; }
      finally { fetchingMe = null; }
    })();
    return fetchingMe;
  }
  async function acceptLogin(data: { access_token: string }) {
    token.value = data.access_token; writeStorage('sessionStorage', TOKEN_KEY, token.value);
    await fetchMe();
    if (!me.value) throw new Error(i18n.global.t('login.sessionInvalid'));
  }
  async function login(method: string, username: string, password: string) {
    error.value = null;
    try { await acceptLogin(await api('/auth/' + method + '/login', { username, password })); }
    catch (cause) { error.value = formatApiError(cause); throw cause; }
  }
  async function startOidc() {
    const data = await api<{ authorization_url: string }>('/auth/oidc/login');
    window.location.assign(data.authorization_url);
  }
  async function handleOidcCallback(code: string, state: string) { await acceptLogin(await api('/auth/oidc/callback', { code, state })); }
  async function startDingTalk(corp?: string) { const data=await api<{authorization_url:string}>('/auth/dingtalk/login'+(corp?'?corp='+encodeURIComponent(corp):''));window.location.assign(data.authorization_url); }
  async function handleDingTalkCallback(code:string,state:string) { await acceptLogin(await api('/auth/dingtalk/callback',{code,state})); }
  async function getProviders() { return api<{ oidc: boolean; ldap: boolean; local: boolean; local_development: boolean; dingtalk:{enabled:boolean;corps:Array<{corp_id:string;name:string}>} }>('/auth/providers'); }
  async function logout() { try { if (token.value) await api('/auth/logout', {}); } finally { clearSession(); } }
  return { token, me, error, isAuthed, permitted, api, service, download: client.download, clearSession,
    loginLocal: (u: string, p: string) => login('local', u, p), loginLdap: (u: string, p: string) => login('ldap', u, p),
    startOidc, handleOidcCallback, startDingTalk, handleDingTalkCallback, fetchMe, getProviders, logout };
});
